import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowLeftIcon,
  CheckIcon,
  XMarkIcon,
  BanknotesIcon,
  ClockIcon
} from '@heroicons/react/24/solid';
import Tilt from 'react-parallax-tilt';
import axios from 'axios';
import api from '../api';

export default function BetConfirmationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { market, side, currentPrice, predictionData } = location.state || {};
  
  const [isConfirming, setIsConfirming] = useState(false);
  const [betAmount, setBetAmount] = useState(50); // Default bet amount
  const [userChips, setUserChips] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [fetchingPrediction, setFetchingPrediction] = useState(true);
  
  // Deal expiration state
  const [timeRemaining, setTimeRemaining] = useState(30); // 30 seconds expiration time
  const [isExpired, setIsExpired] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const timerRef = useRef(null);
  const activePredictionRef = useRef(null); // Store the active prediction to use when placing bet
  
  // Fetch prediction data from API
  const fetchPrediction = async () => {

    if (timerRef.current) clearInterval(timerRef.current);

    if (!predictionData) {
      setFetchingPrediction(false);
      setError("Missing prediction data");
      return;
    }
    
    const { symbol, horizon, apiUrl } = predictionData;
    setFetchingPrediction(true);
    setIsRefreshing(true);
    
    try {
      const res = await fetch(`${apiUrl}/predict?symbol=${symbol}&horizon=${horizon}`);
      if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
      const data = await res.json();
      console.log("🚀 Prediction result:", data);
      setPrediction(data);
      activePredictionRef.current = data; // Store the active prediction
      setIsExpired(false);
      setTimeRemaining(30); // Reset timer to 30 seconds
    } catch (err) {
      console.error("Failed to fetch prediction:", err);
      setError("Failed to load prediction data");
    } finally {
      setFetchingPrediction(false);
      setIsRefreshing(false);
    }
  };
  
  // Initial prediction fetch
  useEffect(() => {
    fetchPrediction();
  }, [predictionData]);
  
  // Setup expiration timer
  useEffect(() => {
    if (prediction && timeRemaining > 0 && !isExpired) {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          const newTime = prev - 1;
          if (newTime <= 0) {
            clearInterval(timerRef.current);
            setIsExpired(true);
            return 0;
          }
          return newTime;
        });
      }, 1000);
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [prediction, isExpired]);
  
  // Auto-refresh when expired
  useEffect(() => {
    if (isExpired && !isRefreshing) {
      fetchPrediction();
    }
  }, [isExpired, isRefreshing]);
  
  // Fetch user profile data to get the chips
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        api.get("/profile")
          .then(({ data }) => {
            console.log("→ [BetConfirmation] got profile data:", data);
            setUserChips(data.chips || 0);
          })
          .catch(err => {
            console.error("Failed to load profile:", err);
            setError("Failed to load profile data");
          })
          .finally(() => {
            // Only set loading to false if we're also done fetching prediction
            if (!fetchingPrediction) {
              setLoading(false);
            }
          });
      } catch (error) {
        console.error("API error:", error);
        if (!fetchingPrediction) {
          setLoading(false);
        }
        setError("Connection error");
      }
    } else {
      if (!fetchingPrediction) {
        setLoading(false);
      }
      setError("Not logged in");
    }
  }, [fetchingPrediction]);

  // Update loading state when both profile and prediction fetches are complete
  useEffect(() => {
    if (!fetchingPrediction) {
      setLoading(false);
    }
  }, [fetchingPrediction]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-black text-gray-300">
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 border-2 border-violet-500 rounded-full relative flex items-center justify-center">
            <div className="w-10 h-10 bg-gradient-to-r from-violet-500 via-pink-500 to-red-500 rounded-full animate-pulse" />
            <div className="absolute inset-0 border-t-2 border-violet-500 rounded-full animate-spin" />
          </div>
          <div className="mt-4 font-mono text-sm">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
              Loading Bet Confirmation…
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (!market || !prediction) {
    // Handle invalid navigation (no data passed)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-black text-gray-300">
        <div className="p-4 sm:p-6 border border-red-500 rounded-lg bg-black bg-opacity-60 max-w-xs sm:max-w-md mx-auto">
          <div className="flex items-center mb-2">
            <span className="text-red-500 font-bold">Error</span>
          </div>
          <div className="text-sm text-gray-300">
            {error || "Invalid bet data. Please return to the market selection."}
          </div>
          <button 
            onClick={() => navigate('/')}
            className="mt-4 px-4 py-2 bg-gradient-to-r from-violet-500 to-pink-500 text-white rounded-lg flex items-center"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Return Home
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-black text-gray-300">
        <div className="p-4 sm:p-6 border border-red-500 rounded-lg bg-black bg-opacity-60 max-w-xs sm:max-w-md mx-auto">
          <div className="flex items-center mb-2">
            <span className="text-red-500 font-bold">Error</span>
          </div>
          <div className="text-sm text-gray-300">
            {error}. Please try again.
          </div>
          <button 
            onClick={() => navigate('/')}
            className="mt-4 px-4 py-2 bg-gradient-to-r from-violet-500 to-pink-500 text-white rounded-lg flex items-center"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Return Home
          </button>
        </div>
      </div>
    );
  }

  const { 
    symbol, 
    current_price, 
    horizon, 
    predicted_time, 
    prob_up, 
    class_direction, 
    predicted_price, 
    reg_direction 
  } = prediction;

  const isUpPrediction = predicted_price > current_price;
  const probabilityPercent = (prob_up * 100).toFixed(2);
  const priceDifference = predicted_price - current_price;
  const percentChange = ((priceDifference / current_price) * 100).toFixed(3);
  
  // Format the predicted time to be more readable
  const formattedPredictedTime = new Date(predicted_time).toLocaleString();
  
  // Calculate potential payout based on probability
  // const odds = isUpPrediction ? 
  //   (1 / prob_up).toFixed(2) : 
  //   (1 / (1 - prob_up)).toFixed(2);

  const odds = "1.5";
    
  const potentialPayout = (betAmount * parseFloat(odds)).toFixed(2);

  const handleConfirmBet = async () => {

    if (isExpired) {
      return alert('Prediction expired — please wait for refresh.');
    }

    if (betAmount > userChips) {
      return alert("You don't have enough chips!");
    }
  
    setIsConfirming(true);
    try {
      // Use the active prediction from the ref to ensure we use the prediction that was shown
      // even if it expired during submission
      const activePrediction = activePredictionRef.current;
      
      // POST /api/bets with your payload
      const { data } = await api.post('/bets', {
        symbol: market.asset,
        side,
        amount: betAmount,
        horizon: predictionData.horizon,
        // You might want to include additional prediction data here if needed
        prediction_data: {
          predicted_price: activePrediction.predicted_price,
          current_price: activePrediction.current_price,
          prob_up: activePrediction.prob_up
        }
      });

      console.log("🏷️ [handleConfirmBet] api.post('/bets') →", data);

      const betIdToPass = data.betId;
      const remainingChipsToPass = data.remainingChips;
      
      // DEBUG→ show me exactly what we’re about to send
      console.log('⏩ Navigating to /bet-success with state:', {
        ...location.state,
        betId: betIdToPass,
        remainingChips: remainingChipsToPass
      });
  
      // Navigate immediately on success, passing betId + new balance
      navigate('/bet-success', {
        state: {
          ...location.state,
          betId: betIdToPass,
          remainingChips: data.remainingChips,
        }
      });
    } catch (err) {
      console.error(err);
      setError('Failed to place bet');
    } finally {
      setIsConfirming(false);
    }
  };  

  // Format time remaining nicely
  const formatTimeRemaining = (seconds) => {
    return `${seconds}s`;
  };

  return (
    <main className="min-h-screen bg-gradient-to-br from-gray-900 to-black text-white font-mono px-4 sm:px-4 pb-12 pt-4 sm:pt-4 -mt-16">
      <div className="max-w-4xl mx-auto">

        {/* Header */}
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-violet-500 via-pink-500 to-red-500 text-center mb-6">
          Confirm Your Bet
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 sm:gap-6 mb-6">
          {/* Balance Card - desktop only */}
          <div className="hidden md:block md:col-span-1">
            <div className="p-1 rounded-lg bg-gradient-to-br from-gray-900 to-black border border-gray-700 shadow-xl h-full">
              <div className="p-6 flex flex-col items-center justify-center h-full">
                <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Your Balance</div>
                <div className="relative">
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-violet-500 to-pink-500 rounded-lg opacity-70 blur" />
                  <div className="relative px-8 py-3 bg-gradient-to-br from-gray-900 to-black border border-gray-700 rounded-lg flex flex-col items-center">
                    <BanknotesIcon className="h-6 w-6 text-pink-400 mb-1" />
                    <div className="font-bold text-2xl text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
                      {userChips.toLocaleString()}
                    </div>
                  </div>
                </div>
                <div className="mt-3 text-xs text-gray-300">Available Chips</div>
              </div>
            </div>
          </div>

          {/* Market Card */}
          <div className="md:col-span-3">
            <div className="p-1 rounded-lg bg-gradient-to-br from-gray-900 to-black border border-gray-700 shadow-xl">
                <div className="p-3 sm:p-4 flex flex-col justify-between rounded-3xl">
                  {/* Deal Expiration Timer */}
                  <div 
                    className={`mb-2 flex justify-between items-center ${
                      timeRemaining <= 5 ? 'animate-pulse' : ''
                    }`}
                  >
                    <div className="flex items-center">
                      <ClockIcon className={`h-4 w-4 ${
                        timeRemaining <= 5 ? 'text-red-400' : 'text-green-400'
                      } mr-1`} />
                      <span className="text-xs text-gray-400">Deal expires in:</span>
                    </div>
                    <div className={`text-xs font-bold px-2 py-1 rounded ${
                      timeRemaining <= 5 
                        ? 'bg-red-500/20 text-red-400' 
                        : 'bg-green-500/20 text-green-400'
                    }`}>
                      {isRefreshing ? 'Refreshing...' : formatTimeRemaining(timeRemaining)}
                    </div>
                  </div>

                  {/* Header */}
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <h3 className="text-lg sm:text-xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">{symbol}</h3>
                      <p className="text-xs text-gray-400">
                        Horizon: <span className="text-white">{horizon}m</span>
                      </p>
                    </div>
{/* Price Direction Arrow */}
<div 
  className={`p-2 rounded-full ${
    predicted_price > current_price
      ? 'bg-gradient-to-tr from-green-400 to-green-600'
      : 'bg-gradient-to-tr from-red-400 to-red-600'
  }`}
>
  {predicted_price > current_price ? (
    <ArrowUpIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
  ) : (
    <ArrowDownIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
  )}
</div>
                  </div>


{/* Prices */}
<div className="mb-4 sm:mb-6">
  <div className="flex justify-between mb-2 sm:mb-3 text-sm sm:text-base">
    <span className="text-gray-400">Current Price:</span>
    <span className="text-white font-semibold">
      ${current_price.toLocaleString(undefined, {
        minimumFractionDigits: current_price < 1 ? 4 : 2,
        maximumFractionDigits: current_price < 1 ? 6 : 4,
      })}
    </span>
  </div>
  
  <div className="flex justify-between mb-2 sm:mb-3 text-sm sm:text-base">
    <span className="text-gray-400">Predicted Price:</span>
    <span className={`font-semibold ${priceDifference >= 0 ? 'text-green-400' : 'text-red-400'}`}>
      ${predicted_price.toLocaleString(undefined, {
        minimumFractionDigits: predicted_price < 1 ? 4 : 2,
        maximumFractionDigits: predicted_price < 1 ? 6 : 4,
      })}
      <span className="ml-1 text-xs">
        ({priceDifference >= 0 ? '+' : ''}{percentChange}%)
      </span>
    </span>
  </div>
  
  <div className="flex justify-between text-sm sm:text-base">
    <span className="text-gray-400">Settles At:</span>
    <span className="text-white text-xs sm:text-sm">{formattedPredictedTime}</span>
  </div>
</div>

                  {/* AI Confidence */}
                  <div className="mb-4 sm:mb-6">
                    <div className="flex justify-between items-center mb-1 text-xs">
                      <span className="text-gray-400">AI Confidence</span>
                      <span className="font-semibold text-white">
                        {probabilityPercent}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-700 h-2 rounded-full overflow-hidden">
                      <div
                        className={`h-2 rounded-full transition-all duration-500 ${
                          isUpPrediction ? 'bg-green-400' : 'bg-red-400'
                        }`}
                        style={{ width: `${probabilityPercent}%` }}
                      />
                    </div>
                  </div>
                  
                  <div className="bg-gray-800/50 p-3 sm:p-4 rounded-xl mb-2 sm:mb-4 text-sm sm:text-base">
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-300">Your prediction:</span>
                    <span className={`font-medium ${predicted_price < current_price ? 'text-green-400' : 'text-red-400'}`}>
                      Price will go {predicted_price < current_price ? 'up' : 'down'}
                    </span>
                  </div>
                    
                    <div className="flex justify-between">
                      <span className="text-gray-300">Odds:</span>
                      <span className="text-white font-medium">{odds}x</span>
                    </div>
                  </div>
                </div>
            </div>
          </div>
        </div>

        {/* Bet Amount Card */}
        <div className="p-1 rounded-lg bg-gradient-to-br from-gray-900 to-black border border-gray-700 shadow-xl mb-6">
          <div className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h2 className="text-lg sm:text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-500 via-pink-500 to-red-500">
                Place Your Bet
              </h2>
              <div className="h-px flex-grow ml-4 bg-gradient-to-r from-violet-500/50 via-pink-500/50 to-transparent" />

            <div className="flex items-center bg-gradient-to-r from-gray-800 to-gray-900 rounded-full py-1.5 px-3 sm:px-4 border border-gray-700">
                <BanknotesIcon className="h-4 w-4 text-pink-400 mr-1 sm:mr-2" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400 font-bold text-sm">
                {userChips.toLocaleString()}
                </span>
            </div>
            </div>

            <div className="mb-4 sm:mb-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3 gap-2 sm:gap-0">
                <span className="text-white font-medium text-sm sm:text-base">Bet Amount:</span>
                <div className="relative">
                  <div className="relative px-3 sm:px-4 py-2 bg-black border border-gray-700 rounded">
                    <span className="text-xl sm:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
                      {betAmount} <span className="text-xs sm:text-sm">CHIPS</span>
                    </span>
                  </div>
                </div>
              </div>
              
              <input
                type="range"
                min="10"
                max={Math.min(1000, userChips)}
                step="10"
                value={betAmount}
                onChange={(e) => setBetAmount(Number(e.target.value))}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer mt-2"
              />
              
              <div className="flex justify-between mt-2 text-xs text-gray-400">
                <span>10 CHIPS</span>
                <span>{Math.min(1000, userChips)} CHIPS</span>
              </div>
              
              {betAmount > userChips && (
                <div className="mt-2 text-red-400 text-xs">
                  You don't have enough chips for this bet!
                </div>
              )}
              
              <div className="mt-4 sm:mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[50, 100, 250, 500].map((amount) => (
                  <button
                    key={amount}
                    onClick={() => setBetAmount(Math.min(amount, userChips))}
                    className={`py-2 rounded border transition ${
                      betAmount === amount
                        ? 'bg-gradient-to-r from-violet-500/30 to-pink-500/30 border-pink-500 text-white'
                        : 'bg-gray-800/50 border-gray-700 text-gray-300 hover:border-pink-500/50'
                    }`}
                  >
                    {amount}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="p-1 rounded-lg bg-gradient-to-br from-gray-900 to-black border border-gray-700 shadow-xl">
              <div className="p-3 sm:p-4 bg-gradient-to-br from-gray-900/90 to-black/90 rounded-lg">
                <div className="flex justify-between items-center mb-3 sm:mb-4 text-sm sm:text-base">
                  <span className="text-gray-300">Risk:</span>
                  <span className="text-white font-medium">{betAmount} CHIPS</span>
                </div>
                
                <div className="flex flex-col sm:flex-row sm:justify-between items-start sm:items-center gap-1 sm:gap-0">
                  <span className="text-gray-300 text-sm sm:text-base">Potential Win:</span>
                  <span className="text-xl sm:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-teal-400">
                    +{potentialPayout} CHIPS
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Confirm/Cancel Buttons */}
        <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 sm:gap-4">
          <button
            onClick={() => navigate(-1)}
            className="py-3 sm:py-4 bg-gradient-to-br from-gray-800 to-gray-900 text-white font-medium rounded-xl flex items-center justify-center transition border border-gray-700 hover:border-red-500/50"
            disabled={isConfirming}
          >
            <XMarkIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-red-400" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-gray-300 to-white text-sm sm:text-base">Cancel</span>
          </button>
          
          <motion.button
            onClick={handleConfirmBet}
            className={`py-3 sm:py-4 bg-gradient-to-br from-black to-gray-900 text-white font-medium rounded-xl flex items-center justify-center transition border border-gray-700 hover:border-green-500/50 ${
              isConfirming || betAmount > userChips ? 'opacity-60 cursor-not-allowed' : ''
            }`}
            disabled={isConfirming || betAmount > userChips || isExpired}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
          {isConfirming ? (
            <>
              <div className="h-4 w-4 sm:h-5 sm:w-5 border-2 border-violet-400 border-t-transparent rounded-full animate-spin mr-2" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400 text-sm sm:text-base">
                Processing...
              </span>
            </>
          ) : isExpired ? (
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400 text-sm sm:text-base">
              Expired
            </span>
          ) : (
            <>
              <CheckIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-green-400" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400 text-sm sm:text-base">
                Place Bet (+{potentialPayout})
              </span>
            </>
          )}

          </motion.button>
        </div>
      </div>
    </main>
  );
}
